import React, { useCallback, useEffect, useState } from 'react'
import Module from '../../../../layout/Module'
import { Box } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import useDevelopmentalRubrics from '../../../../../hooks/developmental-rubrics/useDevelopmentalRubrics'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import { getDevelopmentalRubricById } from '../../../../../services/developmental-rubrics/developmental-rubrics.service'
import {
  updateDevelopmentalRubricForm,
  updateDevelopmentalRubricFormField,
  updateDevelopmentalRubricPreview,
  updateDevelopmentalRubricFormError,
  updateDevelopmentalRubricDialog
} from '../../../../../store/actions/developmental-rubrics.actions'
import { handlerErrorV2 } from '../../../../../utils/errors'
import { Alert } from '@mui/material'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import { toggleAlertModule } from '../../../../../store/actions/alert.actions'
import FormBasicInformation from '../FormBasicInformation/FormBasicInformation'
import FormSubject from '../FormSubject/FormSubject'
import FormLearningContinuum from '../FormLearningContinuum/FormLearningContinuum'
import FormGranularSteps from '../FormGranularSteps/FormGranularSteps'
import FormWorkedExamples from '../FormWorkedExamples/FormWorkedExamples'
import FormGrowthGoals from '../FormGrowthGoals/FormGrowthGoals'
import CardsInformation from '../CardsInformation/CardsInformation'
import useDate from '../../../../../hooks/app/useDate'
import MarkingUtils from '../../../../../utils/marking.utils'
import { isBefore } from 'date-fns'

const Edit = () => {
  const stepsAllowed = [
    'basic-information',
    'subject',
    'learning-continuum',
    'granular-step',
    'worked-examples',
    'growth-goals'
  ]
  const dispatch = useDispatch()
  const today = useSelector((state) => state?.date?.date || new Date())
  const { updateDevelopmentalRubric, isLoading } = useDevelopmentalRubrics()
  const formData = useSelector((state) => state?.dr?.form || {})
  const oldFormData = useSelector((state) => state?.dr?.oldForm || {})
  const permission = useSelector((state) => state?.session?.permissions?.dr || {})
  const alertSave = useSelector((state) => state?.alert?.save || {})
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})

  const { determinateStatus } = useDate()
  const { curriculumId, studentId, id, step } = useParams()
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)
  const [isLoadingSave, toggleIsLoadingSave] = useState(false)

  const redirectStudentId = studentId ? `${studentId}/` : ''
  const basepath = `/dashboard/${redirectStudentId}developmental-rubrics/${curriculumId}`

  const onCancel = () => {
    navigate(`${basepath}`)
  }

  const onMinMaxDate = (key, value, oldFormData) => {
    // const status = determinateStatus(
    //   oldFormData.startDate,
    //   oldFormData.endDate
    // );
    // if (status === "active" && oldFormData?.linkedClasses?.length > 0) {
    //   dispatch(updateDevelopmentalRubricFormField(key, date));
    // } else {
    //   dispatch(updateDevelopmentalRubricFormField(key, value));
    // }
    if (isBefore(today, value)) {
      dispatch(updateDevelopmentalRubricFormField(key, value))
    } else {
      dispatch(updateDevelopmentalRubricFormField(key, today))
    }
  }
  const onChangeAlert = (key, value) => {
    dispatch(updateDevelopmentalRubricFormField(key, value))
    if (key === 'startDate') {
      onMinMaxDate('minEndDate', value, oldFormData)
    }

    if (key === 'endDate') {
      dispatch(updateDevelopmentalRubricFormField('maxStartDate', value))
    }

    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(toggleAlertModule(false, 'Developmental rubrics', 'edit', onSave, alertSave.onDontSave))
    }
  }

  const onSave = async (e) => {
    setError(null)
    e.preventDefault()
    try {
      if (!stepsAllowed.includes(step)) {
        setError({ message: 'Invalid step' })
      }
      let type = step

      toggleIsLoadingSave(true)
      let result = await updateDevelopmentalRubric(curriculumId, id, type, formData)
      if (result.error) {
        if (result.message) {
          setError({ message: result.message })
        } else {
          dispatch(updateDevelopmentalRubricFormError(result))
        }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setError({ message })
    } finally {
      toggleIsLoadingSave(false)
    }
  }

  const onLoad = useCallback(async () => {
    if (id) {
      toggleLoading(true)
      try {
        const response = await getDevelopmentalRubricById(curriculumId, id)
        if (!response?.data && response?.error) {
          setError({
            message: response.error.message || 'An error ocurred'
          })
        } else {
          const formData = response?.data || {}
          formData.lcs = formData?.lcs.map((lc) => {
            return {
              ...lc,
              granularSteps: MarkingUtils.sortGranularSteps(currentCurriculum?.levels, lc.granularSteps)
            }
          })
          const data = {
            ...formData,
            endDate: formData.endDate,
            startDate: formData.startDate
          }
          dispatch(updateDevelopmentalRubricForm(data))
          onMinMaxDate('minEndDate', formData.startDate, formData)
          dispatch(updateDevelopmentalRubricFormField('maxStartDate', formData.endDate))
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        setError(message)
      } finally {
        toggleLoading(false)
      }
    }
  }, [curriculumId, id])

  useEffect(() => {
    onLoad()
  }, [onLoad])
  const textLinkedClasses = formData?.linkedClasses?.length > 0 ? 'unlink' : 'link'

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary flex justify-between'>
        <div />
        <div className='flex items-center justify-end gap-2'>
          {stepsAllowed.includes(step) && (
            <>
              {permission?.[`${textLinkedClasses}ed`] && (
                <IconTooltipButton
                  id={`unlink-link-${id}`}
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title={formData?.linkedClasses?.length > 0 ? 'Link' : 'Unlink'}
                  icon='link'
                  className={`shadow-sm ${formData?.linkedClasses?.length > 0 ? '' : 'opacity-75'}`}
                  style={{
                    color: formData?.linkedClasses?.length > 0 ? 'var(--accent)' : 'var(--secondary)',
                    backgroundColor:
                      formData?.linkedClasses?.length > 0
                        ? 'var(--accent-foreground)'
                        : 'var(--secondary-foreground)',
                    borderRadius: '0.5rem',
                    padding: '0.45rem'
                  }}
                  onClick={() => dispatch(updateDevelopmentalRubricDialog(true, textLinkedClasses, formData))}
                />
              )}
              <div className='px-1' />
              {permission.archived && (
                <IconTooltipButton
                  id={`archive-${id}`}
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Archive'
                  icon='archive'
                  style={{
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--destructive)',
                    color: '#fff',
                    padding: '0.45rem'
                  }}
                  className='shadow-sm'
                  onClick={() => dispatch(updateDevelopmentalRubricDialog(true, 'archive', formData))}
                />
              )}
              {permission.deleted && (
                <IconTooltipButton
                  id={`delete-${id}`}
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Delete'
                  icon='trash-alt'
                  style={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#fff',
                    color: 'var(--destructive)',
                    padding: '0.45rem'
                  }}
                  className='shadow-sm'
                  onClick={() => dispatch(updateDevelopmentalRubricDialog(true, 'delete', formData))}
                />
              )}
              <div className='px-4' />
              {permission.cloned && (
                <IconTooltipButton
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Clone'
                  icon='clone'
                  color='secondary'
                  style={{
                    borderRadius: '0.5rem',
                    color: 'var(--primary)',
                    padding: '0.45rem'
                  }}
                  className='shadow-sm'
                  onClick={() => dispatch(updateDevelopmentalRubricDialog(true, 'clone', formData))}
                />
              )}
              <div className='px-1' />
            </>
          )}
          <AlertSaveButton
            submodule='Developmental rubrics'
            disabled={loading || isLoadingSave || isLoading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm w-full'
            onDontSave={onCancel}
          />
          {permission.updated &&
            stepsAllowed
              .filter(
                (ss) =>
                  !['worked-examples', 'growth-goals', 'granular-step', 'learning-continuum'].includes(ss)
              )
              .includes(step) && (
              <ButtonStartIcon
                disabled={loading || isLoadingSave || isLoading || error?.message}
                loading={isLoadingSave}
                onClick={onSave}
                startIcon={['fal', 'check']}
                color='primary'
                className='w-full'
              >
                Save
              </ButtonStartIcon>
            )}
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer
        loading={isLoadingSave || isLoading}
        classNameWrapper={isLoadingSave || isLoading || loading ? '' : 'overflow-auto'}
      >
        {error?.message && (
          <Alert severity='error' onClose={() => setError(null)} className='absolute left-0 right-0 z-12'>
            {error?.message}
          </Alert>
        )}
        {!loading && (
          <Box className='absolute inset-0'>
            {['learning-continuum', 'granular-step'].includes(step) && (
              <IconTooltipButton
                disabled={loading}
                title='Preview'
                icon='expand-alt'
                classNameIcon='text-xl'
                style={{
                  padding: '0.45rem',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                  position: 'absolute',
                  width: 35,
                  height: 35
                }}
                className='shadow-sm  z-10 right-0 top-0'
                onClick={() => dispatch(updateDevelopmentalRubricPreview('edit'))}
              />
            )}
            {['subject', 'learning-continuum', 'granular-step', 'worked-examples', 'growth-goals'].includes(
              step
            ) && <CardsInformation />}
            {step === 'basic-information' && <FormBasicInformation onChangeAlert={onChangeAlert} />}
            {step === 'subject' && <FormSubject onChangeAlert={onChangeAlert} />}
            {step === 'learning-continuum' && <FormLearningContinuum onChangeAlert={onChangeAlert} />}
            {['granular-step'].includes(step) && (
              <FormGranularSteps type={'updated'} onChangeAlert={onChangeAlert} />
            )}
            {step === 'worked-examples' && <FormWorkedExamples onChangeAlert={onChangeAlert} />}
            {step === 'growth-goals' && <FormGrowthGoals onChangeAlert={onChangeAlert} />}
          </Box>
        )}
      </Module.BodyContainer>
    </>
  )
}

export default Edit
